import React, { useState, useCallback, useEffect } from 'react'
import { Grid } from '@material-ui/core'
import HeaderTitle from '_/components/header-title'
import { Button, Datagrid, Loader } from '@refera/ui-web'
import { navigate, useParams } from '@reach/router'
import { useDispatch } from 'react-redux'
import { useToast } from '_/hooks/use-toast'

import KeyRegistryModal from '_/components/modal/key-registry-modal'
import { getKeyRecords, getServiceOrder } from '_/modules/service-orders/actions'
import { EvidenceOfActionModal } from './evidence-of-action-modal'

import { COLUMNS, AttachmentCell } from './constants'
import { ROUTES } from '_utils/constants'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import useRolePermission from '_/hooks/use-role-permission'

import useStyles, { dataGridStyle } from './styles'

const KeyRegisterView = () => {
  const styles = useStyles()
  const { showToast } = useToast()
  const dispatch = useDispatch()
  const { serviceOrderId } = useParams()
  const { checkUserPermission } = useRolePermission()

  const [isLoading, setIsLoading] = useState(false)
  const [records, setRecords] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const [budgets, setBudgets] = useState([])
  const [activeBudgetId, setActiveBudgetId] = useState(null)
  const [serviceOrder, setServiceOrder] = useState(null)
  const [selectedAttachments, setSelectedAttachment] = useState([])
  const [openEvidenceOfActionModal, setOpenEvidenceOfActionModal] = useState(false)

  const handleBackNavigation = useCallback(() => {
    return navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrderId}`)
  }, [navigate, serviceOrderId, dispatch])

  const fetchRecords = useCallback(() => {
    setIsLoading(true)
    dispatch(
      getKeyRecords(serviceOrderId, {
        ordering: '-is_active_budget, -budget__id, -date_and_time_key_action',
      })
    )
      .then(data => {
        setRecords(data?.results || [])
        setIsLoading(false)
      })
      .catch(() => {
        showToast({ type: 'error', message: 'Ocorreu um erro ao buscar os registros de chaves.' })
        setIsLoading(false)
      })
  }, [dispatch, serviceOrderId, showToast])

  const extractBudgets = useCallback(async () => {
    setIsLoading(true)
    const data = await dispatch(getServiceOrder(serviceOrderId, true))
    setServiceOrder(data)

    if (data?.budget) {
      const budgetList = data.budget
        .filter(budget => {
          return (
            budget.isOriginal === false &&
            budget.tradesman !== null &&
            budget.tradesman !== undefined &&
            budget.released === true
          )
        })
        .map(budget => {
          return {
            id: budget.id,
            is_original: budget.isOriginal,
            released: budget.released,
            company_name: budget.tradesman?.companyName,
            tradesman_id: budget.tradesman?.tradesmanUserId,
          }
        })
      setBudgets(budgetList)
      setActiveBudgetId(data.activeBudget)
    } else {
      setBudgets([])
      setActiveBudgetId(null)
    }

    setIsLoading(false)
  }, [dispatch, serviceOrderId])

  const handleOpenModal = useCallback(
    async (record = null) => {
      setEditingRecord(record)
      setModalOpen(true)
      extractBudgets()
    },
    [extractBudgets]
  )

  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
    setEditingRecord(null)
  }, [])

  const handleRefreshRecords = useCallback(() => {
    fetchRecords()
  }, [fetchRecords])

  useEffect(() => {
    fetchRecords()
    extractBudgets()
  }, [fetchRecords, extractBudgets])

  const columnsWithHandlers = COLUMNS?.map(col =>
    col?.field === 'attachment'
      ? {
          ...col,
          renderCell: params => {
            return (
              <AttachmentCell
                value={params?.value}
                uuid={params?.row?.uuid}
                setOpenModal={setOpenEvidenceOfActionModal}
                setAttachments={setSelectedAttachment}
              />
            )
          },
        }
      : col
  )

  const handleEditProcedure = useCallback(event => {
    handleOpenModal(event.row)
  }, [])

  return (
    <>
      <Loader open={isLoading} hasBackdrop />
      <HeaderTitle title="Registros de chaves" backButtonAction={handleBackNavigation}>
        <Button
          onClick={() =>
            checkUserPermission({
              permission: PERMISSIONS.PROPERTY_KEY_REGISTRIES,
              permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
              functionAccess: handleOpenModal,
            })
          }
        >
          Novo
        </Button>
      </HeaderTitle>

      <Grid className={styles.tableContainer}>
        <Grid className={styles.mainTable}>
          <Datagrid
            loading={isLoading}
            paginationMode="server"
            sx={dataGridStyle}
            rows={records}
            onRowClick={handleEditProcedure}
            columns={columnsWithHandlers}
            checkboxSelection={false}
            disableSelectionOnClick
          />
        </Grid>
      </Grid>
      <KeyRegistryModal
        open={modalOpen}
        onClose={handleCloseModal}
        editData={editingRecord}
        budgets={budgets}
        activeBudgetId={activeBudgetId}
        onRefresh={handleRefreshRecords}
        serviceOrder={serviceOrder}
      />
      <EvidenceOfActionModal
        open={openEvidenceOfActionModal}
        setOpenEvidenceOfActionModal={setOpenEvidenceOfActionModal}
        attachments={selectedAttachments}
        setAttachments={setSelectedAttachment}
      />
    </>
  )
}

export default KeyRegisterView
